import { TaskNouns, UserId } from "@sp-crm/core";
import { RegionVisibility } from "components/ui/region-visibility-tree";
import {
    AdvancedSearchConditionNodeType,
    AdvancedSearchEntityType,
    AdvancedSearchRelativeDateInterval,
    FieldConditionOperator,
    GetCustomListQuery,
    WorkflowActorMode,
    WorkflowEventType,
    WorkflowPayload,
    WorkflowSettingType,
    WorkflowStatus,
} from "generated/graphql";

export type WorkflowTemplateKey =
    | "referralSource"
    | "moveInFollowUp"
    | "clientStatusTask"
    | "clientStatusEmail"
    | "clientEmailOpen"
    | "checkLaterTask"
    | "checkLaterEmail"
    | "inactiveClientTask"
    | "notifyUnselectedCommunities"
    | "homeCareLeadStatusTask"
    | "homeCareLeadStatusEmail"
    | "inactiveHomeCareLeadTask"
    | "homeCareLeadEmailOpen"
    | "homeCareLeadReferralCareStartFollowUp"
    | "referralSourceClosedLostHomeCareLeadTask"
    | "referralContactAddedToOrgIntroEmail";

interface WorkflowTemplateInput {
    name: string;
    description: string;
    currentUserId: UserId;
    clientStatusCustomList: GetCustomListQuery["getCustomList"];
    homeCareLeadStatusCustomList: GetCustomListQuery["getCustomList"] | null;
    regionVisibility: RegionVisibility[];
}

interface WorkflowTemplate {
    key: WorkflowTemplateKey;
    entityType: AdvancedSearchEntityType;
    text: string;
    helpText: string;
    toWorkflow(input: WorkflowTemplateInput): WorkflowPayload;
}

export const workflowTemplates = (
    nouns: TaskNouns,
): Record<WorkflowTemplateKey, WorkflowTemplate> => ({
    referralSource: {
        key: "referralSource",
        entityType: AdvancedSearchEntityType.Client,
        text: "Referral source follow-up email",
        helpText:
            "Send a customizable email to the referral source when a client referral source is chosen",
        toWorkflow: ({ name, description, currentUserId, regionVisibility }) => {
            return {
                name,
                description,
                settings: [
                    {
                        scope: "tenant",
                        key: "emailTemplate",
                        type: WorkflowSettingType.EmailTemplateId,
                        label: "",
                        description: "",
                        defaultValue: "",
                    },
                    {
                        scope: "tenant",
                        key: "bccMyself",
                        type: WorkflowSettingType.Boolean,
                        label: "BCC sender on email",
                        description: "The email sender will receive a copy of the email",
                        defaultValue: "false",
                    },
                ],
                triggerInput: {
                    eventType: WorkflowEventType.EntityUpdated,
                    entityUpdated: {
                        fieldMatchers: [
                            {
                                fieldName: "referralContactId",
                                current: {
                                    specialValue: "$NOT_EMPTY",
                                },
                            },
                        ],
                    },
                },
                attachTo: AdvancedSearchEntityType.Client,
                delay: 0,
                delayRecipe: "10m",
                actionEntities: [
                    {
                        email: {
                            body: "{{settings.emailTemplate}}",
                            subject: "{{settings.emailTemplate}}",
                            to: "{{client.referralContact}}",
                            attachments: "{{settings.emailTemplate}}",
                            bccMyself: "{{settings.bccMyself}}",
                        },
                    },
                ],
                status: WorkflowStatus.Draft,
                defaultActorId: currentUserId,
                actorMode: WorkflowActorMode.AssignedUser,
                regionVisibility: { regions: regionVisibility },
            };
        },
    },
    moveInFollowUp: {
        key: "moveInFollowUp",
        entityType: AdvancedSearchEntityType.Client,
        text: "Move-in follow-up email",
        helpText:
            "Send a customizable email to the client, referral source, or their chosen community after they move in",
        toWorkflow: ({ name, description, currentUserId, regionVisibility }) => {
            return {
                name,
                description,
                settings: [
                    {
                        scope: "tenant",
                        key: "recipients",
                        type: WorkflowSettingType.ClientEmails,
                        label: "Choose recipients",
                        description: "",
                        defaultValue:
                            "{{client.bestContactEntity.email1}},{{client.email}}",
                    },
                    {
                        scope: "tenant",
                        key: "emailTemplate",
                        type: WorkflowSettingType.EmailTemplateId,
                        label: "",
                        description: "",
                        defaultValue: "",
                    },
                    {
                        scope: "tenant",
                        key: "bccMyself",
                        type: WorkflowSettingType.Boolean,
                        label: "BCC sender on email",
                        description: "The email sender will receive a copy of the email",
                        defaultValue: "false",
                    },
                    {
                        scope: "tenant",
                        key: "delay",
                        type: WorkflowSettingType.Number,
                        label: "Days after move to send email",
                        description: "",
                        defaultValue: "7",
                    },
                ],
                triggerInput: {
                    eventType: WorkflowEventType.EntityUpdated,
                    entityUpdated: {
                        fieldMatchers: [
                            {
                                fieldName: "moveTimelineActual",
                                current: {
                                    specialValue: "$NOT_EMPTY",
                                },
                            },
                            {
                                fieldName: "status",
                                current: {
                                    values: ["scheduled", "moved", "invoiced", "paid"],
                                },
                            },
                        ],
                    },
                },
                attachTo: AdvancedSearchEntityType.Client,
                delay: 0,
                delayRecipe: `{{client.moveTimelineActual}}+{{settings.delay}}d`,
                actionEntities: [
                    {
                        email: {
                            body: "{{settings.emailTemplate}}",
                            subject: "{{settings.emailTemplate}}",
                            to: "{{settings.recipients}}",
                            attachments: "{{settings.emailTemplate}}",
                            bccMyself: "{{settings.bccMyself}}",
                        },
                    },
                ],
                status: WorkflowStatus.Draft,
                defaultActorId: currentUserId,
                actorMode: WorkflowActorMode.AssignedUser,
                regionVisibility: { regions: regionVisibility },
            };
        },
    },
    clientStatusTask: {
        key: "clientStatusTask",
        entityType: AdvancedSearchEntityType.Client,
        text: `Create ${nouns.singular} on client status changes`,
        helpText: `Create a customizable ${nouns.singular} when a client's status is changed to one of the selected statuses`,
        toWorkflow: ({ name, description, currentUserId, regionVisibility }) => {
            return {
                name,
                description,
                settings: [
                    {
                        scope: "tenant",
                        key: "description",
                        type: WorkflowSettingType.String,
                        label: `${nouns.titleSingular} description`,
                        description: "",
                        defaultValue: "",
                    },
                    {
                        scope: "tenant",
                        key: "dueDateDays",
                        type: WorkflowSettingType.Number,
                        label: `${nouns.titleSingular} due date offset (days after status change)`,
                        description: "",
                        defaultValue: "7",
                    },
                    {
                        scope: "tenant",
                        key: "assignedTo",
                        type: WorkflowSettingType.User,
                        label: `Assign ${nouns.singular} to (leave blank to use client's assigned user)`,
                        description: "",
                        defaultValue: "",
                    },
                ],
                triggerInput: {
                    eventType: WorkflowEventType.EntityUpdated,
                    entityUpdated: {
                        fieldMatchers: [
                            {
                                fieldName: "status",
                                current: {
                                    values: [],
                                },
                            },
                        ],
                    },
                },
                attachTo: AdvancedSearchEntityType.Client,
                delay: 0,
                delayRecipe: "1m",
                actionEntities: [
                    {
                        task: {
                            text: "{{settings.description}}",
                            dueDate: "+{{settings.dueDateDays}}d",
                            assignedTo: "{{settings.assignedTo}}",
                        },
                    },
                ],
                status: WorkflowStatus.Draft,
                defaultActorId: currentUserId,
                actorMode: WorkflowActorMode.AssignedUser,
                regionVisibility: { regions: regionVisibility },
            };
        },
    },
    clientStatusEmail: {
        key: "clientStatusEmail",
        entityType: AdvancedSearchEntityType.Client,
        text: "Send email on client status changes",
        helpText:
            "Send a customizable email when a client's status is changed to one of the selected statuses",
        toWorkflow: ({ name, description, currentUserId, regionVisibility }) => {
            return {
                name,
                description,
                settings: [
                    {
                        scope: "tenant",
                        key: "recipients",
                        type: WorkflowSettingType.ClientEmails,
                        label: "Choose recipients",
                        description: "",
                        defaultValue:
                            "{{client.bestContactEntity.email1}},{{client.email}}",
                    },
                    {
                        scope: "tenant",
                        key: "emailTemplate",
                        type: WorkflowSettingType.EmailTemplateId,
                        label: "",
                        description: "",
                        defaultValue: "",
                    },
                    {
                        scope: "tenant",
                        key: "bccMyself",
                        type: WorkflowSettingType.Boolean,
                        label: "BCC sender on email",
                        description: "The email sender will receive a copy of the email",
                        defaultValue: "false",
                    },
                ],
                triggerInput: {
                    eventType: WorkflowEventType.EntityUpdated,
                    entityUpdated: {
                        fieldMatchers: [
                            {
                                fieldName: "status",
                                current: {
                                    values: [],
                                },
                            },
                        ],
                    },
                },
                attachTo: AdvancedSearchEntityType.Client,
                delay: 0,
                delayRecipe: "10m",
                actionEntities: [
                    {
                        email: {
                            body: "{{settings.emailTemplate}}",
                            subject: "{{settings.emailTemplate}}",
                            to: "{{settings.recipients}}",
                            attachments: "{{settings.emailTemplate}}",
                            bccMyself: "{{settings.bccMyself}}",
                        },
                    },
                ],
                status: WorkflowStatus.Draft,
                defaultActorId: currentUserId,
                actorMode: WorkflowActorMode.AssignedUser,
                regionVisibility: { regions: regionVisibility },
            };
        },
    },
    clientEmailOpen: {
        key: "clientEmailOpen",
        entityType: AdvancedSearchEntityType.Client,
        text: `Client re-engagement ${nouns.singular}`,
        helpText: `Create follow-up ${nouns.singular} when a client email is opened after a period of inactivity.`,
        toWorkflow: ({ name, description, currentUserId, regionVisibility }) => {
            return {
                name,
                description,
                settings: [
                    {
                        scope: "tenant",
                        key: "description",
                        type: WorkflowSettingType.String,
                        label: `${nouns.titleSingular} description`,
                        description: "",
                        defaultValue: "Follow-up with re-engaged client {{client.name}}",
                    },
                    {
                        scope: "tenant",
                        key: "assignedTo",
                        type: WorkflowSettingType.User,
                        label: `Assign ${nouns.singular} to (leave blank to use client's assigned user)`,
                        description: "",
                        defaultValue: "",
                    },
                ],
                triggerInput: {
                    eventType: WorkflowEventType.EmailOpened,
                    emailOpened: {
                        entityFieldMatchers: [],
                        activityThresholdDays: 21,
                    },
                },
                attachTo: AdvancedSearchEntityType.Client,
                delay: 0,
                delayRecipe: "1m",
                actionEntities: [
                    {
                        task: {
                            text: "{{settings.description}}",
                            dueDate: "+1d",
                            assignedTo: "{{settings.assignedTo}}",
                            notifyOwner: true,
                            notification: {
                                subject: `Client re-engagement ${nouns.singular} assigned to you`,
                            },
                        },
                    },
                ],
                status: WorkflowStatus.Draft,
                defaultActorId: currentUserId,
                actorMode: WorkflowActorMode.AssignedUser,
                regionVisibility: { regions: regionVisibility },
            };
        },
    },
    checkLaterTask: {
        key: "checkLaterTask",
        entityType: AdvancedSearchEntityType.Client,
        text: `Check back later follow-up ${nouns.singular}`,
        helpText: `Create a ${nouns.singular} when a client reaches their Check Back Later date`,
        toWorkflow: ({ name, description, currentUserId, regionVisibility }) => {
            return {
                name,
                description,
                settings: [
                    {
                        scope: "tenant",
                        key: "description",
                        type: WorkflowSettingType.String,
                        label: `${nouns.titleSingular} description`,
                        description: "",
                        defaultValue: "Check back later follow-up: {{client.name}}",
                    },
                    {
                        scope: "tenant",
                        key: "assignedTo",
                        type: WorkflowSettingType.User,
                        label: `Assign ${nouns.singular} to (leave blank to use client's assigned user)`,
                        description: "",
                        defaultValue: "",
                    },
                    {
                        scope: "tenant",
                        key: "delay",
                        type: WorkflowSettingType.Number,
                        label: `Days after check later date to create ${nouns.singular}`,
                        description: "",
                        defaultValue: "1",
                    },
                    {
                        scope: "tenant",
                        key: "dueDateDays",
                        type: WorkflowSettingType.Number,
                        label: `${nouns.titleSingular} due date offset (days after workflow trigger)`,
                        description: "",
                        defaultValue: "1",
                    },
                ],
                triggerInput: {
                    eventType: WorkflowEventType.EntityUpdated,
                    entityUpdated: {
                        fieldMatchers: [
                            {
                                fieldName: "statusCheckLaterDate",
                                current: {
                                    specialValue: "$NOT_EMPTY",
                                },
                            },
                            {
                                fieldName: "status",
                                current: {
                                    values: ["checkLater"],
                                },
                            },
                        ],
                    },
                },
                attachTo: AdvancedSearchEntityType.Client,
                delay: 0,
                delayRecipe: "{{client.statusCheckLaterDate}}+{{settings.delay}}d",
                actionEntities: [
                    {
                        task: {
                            text: "{{settings.description}}",
                            dueDate: "+1d",
                            assignedTo: "{{settings.assignedTo}}",
                            notifyOwner: true,
                            notification: {
                                subject: `Client check back later ${nouns.singular} assigned to you`,
                            },
                        },
                    },
                ],
                status: WorkflowStatus.Draft,
                defaultActorId: currentUserId,
                actorMode: WorkflowActorMode.AssignedUser,
                regionVisibility: { regions: regionVisibility },
            };
        },
    },
    checkLaterEmail: {
        key: "checkLaterEmail",
        entityType: AdvancedSearchEntityType.Client,
        text: "Check back later follow-up email",
        helpText: "Send an email when a client reaches their Check Back Later date",
        toWorkflow: ({ name, description, currentUserId, regionVisibility }) => {
            return {
                name,
                description,
                settings: [
                    {
                        scope: "tenant",
                        key: "recipients",
                        type: WorkflowSettingType.ClientEmails,
                        label: "Choose recipients",
                        description: "",
                        defaultValue:
                            "{{client.bestContactEntity.email1}},{{client.email}}",
                    },
                    {
                        scope: "tenant",
                        key: "emailTemplate",
                        type: WorkflowSettingType.EmailTemplateId,
                        label: "",
                        description: "",
                        defaultValue: "",
                    },
                    {
                        scope: "tenant",
                        key: "bccMyself",
                        type: WorkflowSettingType.Boolean,
                        label: "BCC sender on email",
                        description: "The email sender will receive a copy of the email",
                        defaultValue: "false",
                    },
                    {
                        scope: "tenant",
                        key: "delay",
                        type: WorkflowSettingType.Number,
                        label: "Days after check later date to send email",
                        description: "",
                        defaultValue: "1",
                    },
                ],
                triggerInput: {
                    eventType: WorkflowEventType.EntityUpdated,
                    entityUpdated: {
                        fieldMatchers: [
                            {
                                fieldName: "statusCheckLaterDate",
                                current: {
                                    specialValue: "$NOT_EMPTY",
                                },
                            },
                            {
                                fieldName: "status",
                                current: {
                                    values: ["checkLater"],
                                },
                            },
                        ],
                    },
                },
                attachTo: AdvancedSearchEntityType.Client,
                delay: 0,
                delayRecipe: "{{client.statusCheckLaterDate}}+{{settings.delay}}d",
                actionEntities: [
                    {
                        email: {
                            body: "{{settings.emailTemplate}}",
                            subject: "{{settings.emailTemplate}}",
                            to: "{{settings.recipients}}",
                            attachments: "{{settings.emailTemplate}}",
                            bccMyself: "{{settings.bccMyself}}",
                        },
                    },
                ],
                status: WorkflowStatus.Draft,
                defaultActorId: currentUserId,
                actorMode: WorkflowActorMode.AssignedUser,
                regionVisibility: { regions: regionVisibility },
            };
        },
    },
    inactiveClientTask: {
        key: "inactiveClientTask",
        entityType: AdvancedSearchEntityType.Client,
        text: `Inactive client follow-up ${nouns.singular}`,
        helpText: `Create a ${nouns.singular} when a client in an active state has no activity for a period of time`,
        toWorkflow: ({
            name,
            description,
            currentUserId,
            clientStatusCustomList,
            regionVisibility,
        }) => {
            const activeCategory = clientStatusCustomList.categories.find(
                c => c.key === "Active",
            );
            const ongoingCategory = clientStatusCustomList.categories.find(
                c => c.key === "Ongoing",
            );

            if (!activeCategory || !ongoingCategory) {
                return null;
            }

            return {
                name,
                description,
                settings: [
                    {
                        scope: "tenant",
                        key: "description",
                        type: WorkflowSettingType.String,
                        label: `${nouns.titleSingular} description`,
                        description: "",
                        defaultValue: "Follow-up with inactive client {{client.name}}",
                    },
                    {
                        scope: "tenant",
                        key: "assignedTo",
                        type: WorkflowSettingType.User,
                        label: `Assign ${nouns.singular} to (leave blank to use client's assigned user)`,
                        description: "",
                        defaultValue: "",
                    },
                ],
                triggerInput: {
                    eventType: WorkflowEventType.Schedule,
                    schedule: {
                        cron: "0 12 * * *",
                        entityCondition: {
                            nodeType: AdvancedSearchConditionNodeType.And,
                            children: [
                                {
                                    nodeType: AdvancedSearchConditionNodeType.Or,
                                    children: [
                                        ...clientStatusCustomList.items
                                            .filter(
                                                i =>
                                                    i.customListCategoryId ===
                                                        activeCategory.id &&
                                                    !i.isArchived,
                                            )
                                            .map(i => ({
                                                nodeType:
                                                    AdvancedSearchConditionNodeType.FieldCondition,
                                                fieldName: "statusListItem",
                                                operator:
                                                    FieldConditionOperator.CustomListEquals,
                                                textValue: i.id,
                                            })),
                                        ...clientStatusCustomList.items
                                            .filter(
                                                i =>
                                                    i.customListCategoryId ===
                                                        ongoingCategory.id &&
                                                    !i.isArchived,
                                            )
                                            .map(i => ({
                                                nodeType:
                                                    AdvancedSearchConditionNodeType.FieldCondition,
                                                fieldName: "statusListItem",
                                                operator:
                                                    FieldConditionOperator.CustomListEquals,
                                                textValue: i.id,
                                            })),
                                    ],
                                },
                                {
                                    nodeType:
                                        AdvancedSearchConditionNodeType.FieldCondition,
                                    fieldName: "updatedAt",
                                    operator: FieldConditionOperator.DateOnOrBefore,
                                    relativeDate: {
                                        interval: AdvancedSearchRelativeDateInterval.Day,
                                        value: -30,
                                    },
                                },
                            ],
                        },
                    },
                },
                attachTo: AdvancedSearchEntityType.Client,
                delay: 0,
                delayRecipe: null,
                actionEntities: [
                    {
                        task: {
                            text: "{{settings.description}}",
                            dueDate: "+1d",
                            assignedTo: "{{settings.assignedTo}}",
                            notifyOwner: true,
                            notification: {
                                subject: `Inactive client follow-up ${nouns.singular} assigned to you`,
                            },
                        },
                    },
                ],
                status: WorkflowStatus.Draft,
                defaultActorId: currentUserId,
                actorMode: WorkflowActorMode.AssignedUser,
                regionVisibility: { regions: regionVisibility },
            };
        },
    },
    notifyUnselectedCommunities: {
        key: "notifyUnselectedCommunities",
        entityType: AdvancedSearchEntityType.Client,
        text: "Notify unselected communities",
        helpText:
            "Send an email to each community that was not chosen when a community is selected for a client",
        toWorkflow: ({ name, description, currentUserId, regionVisibility }) => {
            return {
                name,
                description,
                settings: [
                    {
                        scope: "tenant",
                        key: "emailTemplate",
                        type: WorkflowSettingType.EmailTemplateId,
                        label: "",
                        description: "",
                        defaultValue: "",
                    },
                    {
                        scope: "tenant",
                        key: "bccMyself",
                        type: WorkflowSettingType.Boolean,
                        label: "BCC sender on email",
                        description: "The email sender will receive a copy of the email",
                        defaultValue: "false",
                    },
                ],
                triggerInput: {
                    eventType: WorkflowEventType.EntityUpdated,
                    entityUpdated: {
                        fieldMatchers: [
                            {
                                fieldName: "chosenCommunityId",
                                current: {
                                    specialValue: "$NOT_EMPTY",
                                },
                            },
                            {
                                fieldName: "status",
                                current: {
                                    values: ["scheduled", "moved", "invoiced", "paid"],
                                },
                            },
                        ],
                    },
                },
                attachTo: AdvancedSearchEntityType.Client,
                delay: 0,
                delayRecipe: "1m",
                actionEntities: [
                    {
                        email: {
                            body: "{{settings.emailTemplate}}",
                            subject: "{{settings.emailTemplate}}",
                            to: "",
                            discreteTo: "referredUnselectedCommunities",
                            attachments: "{{settings.emailTemplate}}",
                            bccMyself: "{{settings.bccMyself}}",
                        },
                    },
                ],
                status: WorkflowStatus.Draft,
                defaultActorId: currentUserId,
                actorMode: WorkflowActorMode.AssignedUser,
                regionVisibility: { regions: regionVisibility },
            };
        },
    },
    homeCareLeadStatusTask: {
        key: "homeCareLeadStatusTask",
        entityType: AdvancedSearchEntityType.HomeCareLead,
        text: `Create ${nouns.singular} on home care client status changes`,
        helpText: `Create a customizable ${nouns.singular} when a home care client's status is changed to one of the selected statuses`,
        toWorkflow: ({ name, description, currentUserId, regionVisibility }) => {
            return {
                name,
                description,
                settings: [
                    {
                        scope: "tenant",
                        key: "description",
                        type: WorkflowSettingType.String,
                        label: `${nouns.titleSingular} description`,
                        description: "",
                        defaultValue: "",
                    },
                    {
                        scope: "tenant",
                        key: "dueDateDays",
                        type: WorkflowSettingType.Number,
                        label: `${nouns.titleSingular} due date offset (days after status change)`,
                        description: "",
                        defaultValue: "7",
                    },
                    {
                        scope: "tenant",
                        key: "assignedTo",
                        type: WorkflowSettingType.User,
                        label: `Assign ${nouns.singular} to (leave blank to use assigned user)`,
                        description: "",
                        defaultValue: "",
                    },
                ],
                triggerInput: {
                    eventType: WorkflowEventType.EntityUpdated,
                    entityUpdated: {
                        fieldMatchers: [
                            {
                                fieldName: "statusListItem",
                                current: {
                                    values: [],
                                },
                            },
                        ],
                    },
                },
                attachTo: AdvancedSearchEntityType.HomeCareLead,
                delay: 0,
                delayRecipe: "1m",
                actionEntities: [
                    {
                        task: {
                            text: "{{settings.description}}",
                            dueDate: "+{{settings.dueDateDays}}d",
                            assignedTo: "{{settings.assignedTo}}",
                        },
                    },
                ],
                status: WorkflowStatus.Draft,
                defaultActorId: currentUserId,
                actorMode: WorkflowActorMode.AssignedUser,
                regionVisibility: { regions: regionVisibility },
            };
        },
    },
    referralSourceClosedLostHomeCareLeadTask: {
        key: "referralSourceClosedLostHomeCareLeadTask",
        entityType: AdvancedSearchEntityType.HomeCareLead,
        text: `Referral source follow-up ${nouns.singular} on home care client status changes`,
        helpText: `Create a ${nouns.singular} to follow up with the referral source when a home care client's status is changed to one of the selected statuses`,
        toWorkflow: ({
            name,
            description,
            currentUserId,
            homeCareLeadStatusCustomList,
            regionVisibility,
        }) => {
            if (!homeCareLeadStatusCustomList) {
                return null;
            }

            return {
                name,
                description,
                settings: [
                    {
                        scope: "tenant",
                        key: "description",
                        type: WorkflowSettingType.String,
                        label: `${nouns.titleSingular} description`,
                        description: "",
                        defaultValue:
                            "Follow up with referral source {{homeCareLead.referralContact.contactEntity.name}} about {{homeCareLead.selfContactEntity.name}}'s status change",
                    },
                    {
                        scope: "tenant",
                        key: "dueDateDays",
                        type: WorkflowSettingType.Number,
                        label: `${nouns.titleSingular} due date offset (days after status change)`,
                        description: "",
                        defaultValue: "7",
                    },
                    {
                        scope: "tenant",
                        key: "assignedTo",
                        type: WorkflowSettingType.User,
                        label: `Assign ${nouns.singular} to (leave blank to use assigned user)`,
                        description: "",
                        defaultValue: "",
                    },
                ],
                triggerInput: {
                    eventType: WorkflowEventType.EntityUpdated,
                    entityUpdated: {
                        fieldMatchers: [
                            {
                                fieldName: "statusListItem",
                                current: {
                                    values: [],
                                },
                            },
                        ],
                    },
                },
                attachTo: AdvancedSearchEntityType.HomeCareLead,
                delay: 0,
                delayRecipe: "1m",
                actionEntities: [
                    {
                        task: {
                            text: "{{settings.description}}",
                            dueDate: "+{{settings.dueDateDays}}d",
                            assignedTo: "{{settings.assignedTo}}",
                        },
                    },
                ],
                status: WorkflowStatus.Draft,
                defaultActorId: currentUserId,
                actorMode: WorkflowActorMode.AssignedUser,
                regionVisibility: { regions: regionVisibility },
            };
        },
    },
    homeCareLeadStatusEmail: {
        key: "homeCareLeadStatusEmail",
        entityType: AdvancedSearchEntityType.HomeCareLead,
        text: "Send email on home care client status changes",
        helpText:
            "Send a customizable email when a home care client's status is changed to one of the selected statuses",
        toWorkflow: ({ name, description, currentUserId, regionVisibility }) => {
            return {
                name,
                description,
                settings: [
                    {
                        scope: "tenant",
                        key: "recipients",
                        type: WorkflowSettingType.ClientEmails,
                        label: "Choose recipients",
                        description: "",
                        defaultValue:
                            "{{homeCareLead.bestContactEntity.email1}},{{homeCareLead.selfContactEntity.email1}}",
                    },
                    {
                        scope: "tenant",
                        key: "emailTemplate",
                        type: WorkflowSettingType.EmailTemplateId,
                        label: "",
                        description: "",
                        defaultValue: "",
                    },
                    {
                        scope: "tenant",
                        key: "bccMyself",
                        type: WorkflowSettingType.Boolean,
                        label: "BCC sender on email",
                        description: "The email sender will receive a copy of the email",
                        defaultValue: "false",
                    },
                ],
                triggerInput: {
                    eventType: WorkflowEventType.EntityUpdated,
                    entityUpdated: {
                        fieldMatchers: [
                            {
                                fieldName: "statusListItem",
                                current: {
                                    values: [],
                                },
                            },
                        ],
                    },
                },
                attachTo: AdvancedSearchEntityType.HomeCareLead,
                delay: 0,
                delayRecipe: "10m",
                actionEntities: [
                    {
                        email: {
                            body: "{{settings.emailTemplate}}",
                            subject: "{{settings.emailTemplate}}",
                            to: "{{settings.recipients}}",
                            attachments: "{{settings.emailTemplate}}",
                            bccMyself: "{{settings.bccMyself}}",
                        },
                    },
                ],
                status: WorkflowStatus.Draft,
                defaultActorId: currentUserId,
                actorMode: WorkflowActorMode.AssignedUser,
                regionVisibility: { regions: regionVisibility },
            };
        },
    },
    inactiveHomeCareLeadTask: {
        key: "inactiveHomeCareLeadTask",
        entityType: AdvancedSearchEntityType.HomeCareLead,
        text: `Inactive home care client follow-up ${nouns.singular}`,
        helpText: `Create a ${nouns.singular} when a home care client in an active state has no activity for a period of time`,
        toWorkflow: ({
            name,
            description,
            currentUserId,
            homeCareLeadStatusCustomList,
            regionVisibility,
        }) => {
            if (!homeCareLeadStatusCustomList) {
                return null;
            }

            const activeCategory = homeCareLeadStatusCustomList.categories.find(
                c => c.key === "Active",
            );

            if (!activeCategory) {
                return null;
            }

            return {
                name,
                description,
                settings: [
                    {
                        scope: "tenant",
                        key: "description",
                        type: WorkflowSettingType.String,
                        label: `${nouns.titleSingular} description`,
                        description: "",
                        defaultValue:
                            "Follow-up with inactive home care client {{homeCareLead.selfContactEntity.name}}",
                    },
                    {
                        scope: "tenant",
                        key: "assignedTo",
                        type: WorkflowSettingType.User,
                        label: `Assign ${nouns.singular} to (leave blank to use assigned user)`,
                        description: "",
                        defaultValue: "",
                    },
                ],
                triggerInput: {
                    eventType: WorkflowEventType.Schedule,
                    schedule: {
                        cron: "0 12 * * *",
                        entityCondition: {
                            nodeType: AdvancedSearchConditionNodeType.And,
                            children: [
                                {
                                    nodeType: AdvancedSearchConditionNodeType.Or,
                                    children: homeCareLeadStatusCustomList.items
                                        .filter(
                                            i =>
                                                i.customListCategoryId ===
                                                    activeCategory.id && !i.isArchived,
                                        )
                                        .map(i => ({
                                            nodeType:
                                                AdvancedSearchConditionNodeType.FieldCondition,
                                            fieldName: "statusListItem",
                                            operator:
                                                FieldConditionOperator.CustomListEquals,
                                            textValue: i.id,
                                        })),
                                },
                                {
                                    nodeType:
                                        AdvancedSearchConditionNodeType.FieldCondition,
                                    fieldName: "updatedAt",
                                    operator: FieldConditionOperator.DateOnOrBefore,
                                    relativeDate: {
                                        interval: AdvancedSearchRelativeDateInterval.Day,
                                        value: -30,
                                    },
                                },
                            ],
                        },
                    },
                },
                attachTo: AdvancedSearchEntityType.HomeCareLead,
                delay: 0,
                delayRecipe: null,
                actionEntities: [
                    {
                        task: {
                            text: "{{settings.description}}",
                            dueDate: "+1d",
                            assignedTo: "{{settings.assignedTo}}",
                            notifyOwner: true,
                            notification: {
                                subject: `Inactive home care client follow-up ${nouns.singular} assigned to you`,
                            },
                        },
                    },
                ],
                status: WorkflowStatus.Draft,
                defaultActorId: currentUserId,
                actorMode: WorkflowActorMode.AssignedUser,
                regionVisibility: { regions: regionVisibility },
            };
        },
    },
    homeCareLeadEmailOpen: {
        key: "homeCareLeadEmailOpen",
        entityType: AdvancedSearchEntityType.HomeCareLead,
        text: `Home care client re-engagement ${nouns.singular}`,
        helpText: `Create follow-up ${nouns.singular} when a home care client email is opened after a period of inactivity.`,
        toWorkflow: ({ name, description, currentUserId, regionVisibility }) => {
            return {
                name,
                description,
                settings: [
                    {
                        scope: "tenant",
                        key: "description",
                        type: WorkflowSettingType.String,
                        label: `${nouns.titleSingular} description`,
                        description: "",
                        defaultValue:
                            "Follow-up with re-engaged home care client {{homeCareLead.selfContactEntity.name}}",
                    },
                    {
                        scope: "tenant",
                        key: "assignedTo",
                        type: WorkflowSettingType.User,
                        label: `Assign ${nouns.singular} to (leave blank to use assigned user)`,
                        description: "",
                        defaultValue: "",
                    },
                ],
                triggerInput: {
                    eventType: WorkflowEventType.EmailOpened,
                    emailOpened: {
                        entityFieldMatchers: [],
                        activityThresholdDays: 21,
                    },
                },
                attachTo: AdvancedSearchEntityType.HomeCareLead,
                delay: 0,
                delayRecipe: "1m",
                actionEntities: [
                    {
                        task: {
                            text: "{{settings.description}}",
                            dueDate: "+1d",
                            assignedTo: "{{settings.assignedTo}}",
                            notifyOwner: true,
                            notification: {
                                subject: `Home care client re-engagement ${nouns.singular} assigned to you`,
                            },
                        },
                    },
                ],
                status: WorkflowStatus.Draft,
                defaultActorId: currentUserId,
                actorMode: WorkflowActorMode.AssignedUser,
                regionVisibility: { regions: regionVisibility },
            };
        },
    },
    homeCareLeadReferralCareStartFollowUp: {
        key: "homeCareLeadReferralCareStartFollowUp",
        entityType: AdvancedSearchEntityType.HomeCareLead,
        text: "Home care referral source follow-up email when care starts",
        helpText:
            "Send a customizable email to the referral source when a home care client starts care, based on start date and client status",
        toWorkflow: ({ name, description, currentUserId, regionVisibility }) => {
            return {
                name,
                description,
                settings: [
                    {
                        scope: "tenant",
                        key: "recipients",
                        type: WorkflowSettingType.ClientEmails,
                        label: "Choose recipients",
                        description: "",
                        defaultValue:
                            "{{homeCareLead.referralContact.contactEntity.email1}}",
                    },
                    {
                        scope: "tenant",
                        key: "emailTemplate",
                        type: WorkflowSettingType.EmailTemplateId,
                        label: "",
                        description: "",
                        defaultValue: "",
                    },
                    {
                        scope: "tenant",
                        key: "bccMyself",
                        type: WorkflowSettingType.Boolean,
                        label: "BCC sender on email",
                        description: "The email sender will receive a copy of the email",
                        defaultValue: "false",
                    },
                    {
                        scope: "tenant",
                        key: "delay",
                        type: WorkflowSettingType.Number,
                        label: "Days after entering care to send email",
                        description: "",
                        defaultValue: "7",
                    },
                ],
                triggerInput: {
                    eventType: WorkflowEventType.EntityUpdated,
                    entityUpdated: {
                        fieldMatchers: [
                            {
                                fieldName: "actualStartDate",
                                current: {
                                    specialValue: "$NOT_EMPTY",
                                },
                            },
                            {
                                fieldName: "referralContactId",
                                current: {
                                    specialValue: "$NOT_EMPTY",
                                },
                            },
                            {
                                fieldName: "statusListItem",
                                current: {
                                    values: ["hcStartCare", "hcClosedWon"],
                                },
                            },
                        ],
                    },
                },
                attachTo: AdvancedSearchEntityType.HomeCareLead,
                delay: 0,
                delayRecipe: `{{homeCareLead.actualStartDate}}+{{settings.delay}}d`,
                actionEntities: [
                    {
                        email: {
                            body: "{{settings.emailTemplate}}",
                            subject: "{{settings.emailTemplate}}",
                            to: "{{settings.recipients}}",
                            attachments: "{{settings.emailTemplate}}",
                            bccMyself: "{{settings.bccMyself}}",
                        },
                    },
                ],
                status: WorkflowStatus.Draft,
                defaultActorId: currentUserId,
                actorMode: WorkflowActorMode.AssignedUser,
                regionVisibility: { regions: regionVisibility },
            };
        },
    },
    referralContactAddedToOrgIntroEmail: {
        key: "referralContactAddedToOrgIntroEmail",
        entityType: AdvancedSearchEntityType.ReferenceContact,
        text: "Send introduction email to new org contacts",
        helpText:
            "Send a customizable introduction email when a referral contact is first attached to an organization",
        toWorkflow: ({ name, description, currentUserId, regionVisibility }) => {
            return {
                name,
                description,
                settings: [
                    {
                        scope: "tenant",
                        key: "emailTemplate",
                        type: WorkflowSettingType.EmailTemplateId,
                        label: "",
                        description: "",
                        defaultValue: "",
                    },
                    {
                        scope: "tenant",
                        key: "bccMyself",
                        type: WorkflowSettingType.Boolean,
                        label: "BCC sender on email",
                        description: "The email sender will receive a copy of the email",
                        defaultValue: "false",
                    },
                ],
                triggerInput: {
                    eventType: WorkflowEventType.EntityUpdated,
                    entityUpdated: {
                        fieldMatchers: [
                            {
                                fieldName: "businessId",
                                current: {
                                    specialValue: "$NOT_EMPTY",
                                },
                            },
                            {
                                fieldName: "businessId",
                                previous: {
                                    specialValue: "$EMPTY",
                                },
                            },
                        ],
                    },
                },
                attachTo: AdvancedSearchEntityType.ReferenceContact,
                delay: 0,
                delayRecipe: "10m",
                actionEntities: [
                    {
                        email: {
                            body: "{{settings.emailTemplate}}",
                            subject: "{{settings.emailTemplate}}",
                            to: "{{referenceContact.contactEntity.email1}}",
                            attachments: "{{settings.emailTemplate}}",
                            bccMyself: "{{settings.bccMyself}}",
                        },
                    },
                ],
                status: WorkflowStatus.Draft,
                defaultActorId: currentUserId,
                actorMode: WorkflowActorMode.AssignedUser,
                regionVisibility: { regions: regionVisibility },
            };
        },
    },
});
